import { Link } from 'react-router-dom';
import { Button } from '@ridemaadi/ui';
import { Logo } from '../components/Logo';
import { PublicLayout } from '../components/layout/PublicLayout';
import { PublicCta } from '../features/public/PublicCta';
import { startGoogleSignIn } from '../features/public/site';

const EXPLORE_LINKS = [
  { to: '/features', label: 'Features' },
  { to: '/how-it-works', label: 'How It Works' },
  { to: '/for-riders', label: 'For Riders' },
  { to: '/for-riding-groups', label: 'For Riding Groups' },
  { to: '/about', label: 'About' },
] as const;

/** Static-friendly marketing landing body (used for prerender and client). */
export function LandingPageContent() {
  return (
    <PublicLayout onSignIn={() => startGoogleSignIn()}>
      <section
        className="border-b border-border/40 bg-gradient-to-b from-primary/5 to-bg px-4 py-12 sm:py-16"
        aria-labelledby="landing-heading"
      >
        <div className="mx-auto flex max-w-app flex-col items-center gap-8 text-center">
          <Logo variant="pageload" priority className="max-w-[min(280px,80vw)]" />
          <div className="space-y-4">
            <p className="text-sm font-semibold uppercase tracking-wide text-primary">RideMaadi</p>
            <h1 id="landing-heading" className="text-2xl font-bold tracking-tight sm:text-4xl">
              Motorcycle Riding &amp; Group Ride App
            </h1>
            <p className="mx-auto max-w-2xl text-base leading-relaxed text-muted sm:text-lg">
              RideMaadi helps motorcycle riders discover riding communities, create groups, organize
              rides and connect with fellow riders — whether you ride with a club, a weekend crew, or
              a touring group.
            </p>
          </div>
          <div className="w-full max-w-md space-y-3">
            <Button
              fullWidth
              size="lg"
              className="rounded-2xl shadow-md shadow-primary/25"
              onClick={() => startGoogleSignIn()}
            >
              Sign in with Google
            </Button>
            <p className="text-xs text-muted">
              Free to use. Sign in to plan rides, join groups, and manage your rider profile.
            </p>
          </div>
        </div>
      </section>

      <section className="px-4 py-12 sm:py-16" aria-labelledby="community-heading">
        <div className="mx-auto max-w-app space-y-4">
          <h2 id="community-heading" className="text-xl font-bold sm:text-2xl">
            Find Your Riding Community
          </h2>
          <p className="max-w-3xl text-base leading-relaxed text-muted">
            Looking for fellow motorcycle riders or a group to ride with? RideMaadi helps you discover
            riding communities and connect with riders who share your passion for the road — without
            juggling scattered chat threads.
          </p>
          <p>
            <Link to="/for-riders" className="text-sm font-medium text-primary hover:underline">
              Learn more for riders
            </Link>
          </p>
        </div>
      </section>

      <section
        className="border-y border-border/40 bg-surface-alt/50 px-4 py-12 sm:py-16"
        aria-labelledby="organize-heading"
      >
        <div className="mx-auto max-w-app space-y-4">
          <h2 id="organize-heading" className="text-xl font-bold sm:text-2xl">
            Create and Organize Motorcycle Rides
          </h2>
          <p className="max-w-3xl text-base leading-relaxed text-muted">
            Plan routes, set meeting points, and share ride details so every rider knows the plan before
            wheels roll. RideMaadi is a motorcycle ride planning app built for real group rides — from
            breakfast runs to multi-day tours.
          </p>
          <p>
            <Link to="/features" className="text-sm font-medium text-primary hover:underline">
              See all features
            </Link>
            {' · '}
            <Link to="/how-it-works" className="text-sm font-medium text-primary hover:underline">
              How it works
            </Link>
          </p>
        </div>
      </section>

      <section className="px-4 py-12 sm:py-16" aria-labelledby="groups-heading">
        <div className="mx-auto max-w-app space-y-4">
          <h2 id="groups-heading" className="text-xl font-bold sm:text-2xl">
            Built for Motorcycle Riding Groups
          </h2>
          <p className="max-w-3xl text-base leading-relaxed text-muted">
            Motorcycle clubs and riding groups need more than a group chat. RideMaadi gives captains and
            organizers the tools to invite members, assign roles, and keep every group ride on track.
          </p>
          <p>
            <Link
              to="/for-riding-groups"
              className="text-sm font-medium text-primary hover:underline"
            >
              Learn more for riding groups
            </Link>
          </p>
        </div>
      </section>

      <section
        className="border-y border-border/40 bg-surface-alt/50 px-4 py-12 sm:py-16"
        aria-labelledby="ride-together-heading"
      >
        <div className="mx-auto max-w-app space-y-4">
          <h2 id="ride-together-heading" className="text-xl font-bold sm:text-2xl">
            Ride Together
          </h2>
          <p className="max-w-3xl text-base leading-relaxed text-muted">
            Sign in with Google, join a ride, follow the captain, and check in at meeting points along
            the way. RideMaadi keeps your group aligned from kick-off to finish line.
          </p>
          <p>
            <Link to="/how-it-works" className="text-sm font-medium text-primary hover:underline">
              See how RideMaadi works
            </Link>
          </p>
        </div>
      </section>

      <section className="px-4 py-12 sm:py-16" aria-labelledby="regions-heading">
        <div className="mx-auto max-w-app space-y-4">
          <h2 id="regions-heading" className="text-xl font-bold sm:text-2xl">
            For riders and communities worldwide
          </h2>
          <p className="max-w-3xl text-base leading-relaxed text-muted">
            RideMaadi is built for motorcycle riders and riding communities — including riders across
            India and Europe who want a dedicated app to plan group rides and grow their riding circle.
          </p>
          <p>
            <Link to="/about" className="text-sm font-medium text-primary hover:underline">
              About RideMaadi
            </Link>
          </p>
        </div>
      </section>

      <section className="border-t border-border/40 px-4 py-10" aria-label="Explore RideMaadi">
        <nav className="mx-auto flex max-w-app flex-wrap gap-x-5 gap-y-2 text-sm">
          {EXPLORE_LINKS.map(({ to, label }) => (
            <Link key={to} to={to} className="font-medium text-primary hover:underline">
              {label}
            </Link>
          ))}
        </nav>
      </section>

      <section className="px-4 pb-12 sm:pb-16">
        <div className="mx-auto max-w-app">
          <PublicCta />
        </div>
      </section>
    </PublicLayout>
  );
}
